import { supabase } from "@/integrations/supabase/client";

export type AdminRole = "admin" | "super_admin";

export type UserAccessContext = {
  onboardingCompleted: boolean;
  profileRole: string | null;
  adminRole: AdminRole | null;
  isAdminLevel: boolean;
  isSuperAdmin: boolean;
};

export const getUserAccessContext = async (userId: string): Promise<UserAccessContext> => {
  const [profileResult, rolesResult] = await Promise.all([
    supabase
      .from("profiles")
      .select(
        "onboarding_completed, role, full_name, gender, country, level, school_class, subjects, study_language"
      )
      .eq("id", userId)
      .maybeSingle(),
    (supabase as any).from("user_roles").select("role").eq("user_id", userId).in("role", ["admin", "super_admin"]),
  ]);

  const roleRows = ((rolesResult.data as Array<{ role: AdminRole }> | null) ?? []);
  const adminRole: AdminRole | null = roleRows.some((row) => row.role === "super_admin")
    ? "super_admin"
    : roleRows.some((row) => row.role === "admin")
      ? "admin"
      : null;

  const profile = (profileResult.data ?? null) as any;
  const flag = Boolean(profile?.onboarding_completed);
  const role = profile?.role ?? null;

  // Super admins bypass onboarding completion check
  const requiresFieldCheck = !!profile && adminRole !== "super_admin";

  const has = (v: any) => typeof v === "string" && v.trim().length > 0;
  let fieldsComplete = true;
  if (requiresFieldCheck) {
    // Common required fields for everyone
    if (!has(profile.full_name) || !has(profile.gender)) fieldsComplete = false;
    // Student/learner specific fields
    if (fieldsComplete && (role === "eleve" || role === "etudiant")) {
      if (
        !has(profile.country) ||
        !has(profile.level) ||
        !has(profile.school_class) ||
        !has(profile.study_language) ||
        !Array.isArray(profile.subjects) ||
        profile.subjects.length === 0
      ) {
        fieldsComplete = false;
      }
    }
  }

  return {
    onboardingCompleted: flag && fieldsComplete,
    profileRole: role,
    adminRole,
    isAdminLevel: Boolean(adminRole),
    isSuperAdmin: adminRole === "super_admin",
  };
};

export const resolveAuthenticatedHome = (access: UserAccessContext) => {
  if (access.isSuperAdmin) return "/admin";
  if (!access.onboardingCompleted) return "/onboarding";
  if (access.profileRole === "parent") return "/parent-dashboard";
  if (access.profileRole === "enseignant") return "/teacher-dashboard";
  return "/dashboard";
};